import { daysBetween, type IsoDate } from './dates.ts';
import { computeNetWorth, type RateLookup } from './networth.ts';
import type { BalancePart } from './ownership.ts';
import type { LedgerAccount, LedgerTransaction } from './types.ts';

export type NetWorthPoint = {
  readonly date: IsoDate;
  readonly assetsMinor: bigint;
  /** Positive amount owed. */
  readonly liabilitiesMinor: bigint;
  readonly netMinor: bigint;
};

export type HistoryAccount = LedgerAccount & {
  /** Before this date the account is not in the records and counts as nothing. */
  readonly openingDate: IsoDate;
};

/**
 * Net worth on each of the given dates, walking back from today's balances: an account's
 * balance on a day is its balance now minus everything booked after that day. Loans whose
 * balance grows with interest are asked from `balanceOn` instead (DECISIONS D-023). Today's
 * exchange rates are used for every date — the chart shows what the money is worth now.
 * With `part`, each day's balance is split first, so one person's line follows their share.
 */
export function netWorthHistory(
  accounts: readonly HistoryAccount[],
  transactions: readonly LedgerTransaction[],
  dates: readonly IsoDate[],
  baseCurrency: string,
  rates: RateLookup,
  balanceOn?: (account: HistoryAccount, date: IsoDate) => bigint | null,
  part?: BalancePart,
): NetWorthPoint[] {
  const byAccount = new Map<string, LedgerTransaction[]>();
  for (const t of transactions) {
    if (t.status === 'REVERSED' || t.status === 'CANCELLED') continue;
    const list = byAccount.get(t.accountId);
    if (list) list.push(t);
    else byAccount.set(t.accountId, [t]);
  }
  for (const list of byAccount.values()) list.sort((a, b) => (a.bookingDate < b.bookingDate ? 1 : a.bookingDate > b.bookingDate ? -1 : 0));

  return [...dates]
    .sort()
    .map((date) => {
      const balances = accounts
        .filter((account) => account.openingDate <= date)
        .map((account) => {
          const special = balanceOn?.(account, date) ?? null;
          if (special !== null) return { ...account, balanceMinor: special };
          let later = 0n;
          for (const t of byAccount.get(account.id) ?? []) {
            if (t.bookingDate <= date) break;
            later += t.amountMinor;
          }
          return { ...account, balanceMinor: account.balanceMinor - later };
        });
      const worth = computeNetWorth(balances, baseCurrency, rates, part);
      return { date, assetsMinor: worth.assetsMinor, liabilitiesMinor: worth.liabilitiesMinor, netMinor: worth.netMinor };
    });
}

/**
 * The value of something the owner values by hand (a car) on a day: straight lines between the
 * values they gave — the price on the opening date, then each new value — since a car loses value
 * day by day, not on the day it is written down. After the last value it stays there.
 */
export function valuedAssetOn(
  openingDate: IsoDate,
  openingMinor: bigint,
  changes: readonly { readonly date: IsoDate; readonly amountMinor: bigint }[],
  date: IsoDate,
): bigint | null {
  if (date < openingDate) return null;
  const byDate = new Map<IsoDate, bigint>();
  for (const c of changes) if (c.date >= openingDate) byDate.set(c.date, (byDate.get(c.date) ?? 0n) + c.amountMinor);
  let previous = { date: openingDate, value: openingMinor };
  let value = openingMinor;
  for (const day of [...byDate.keys()].sort()) {
    value += byDate.get(day)!;
    if (date < day) {
      const span = daysBetween(previous.date, day);
      const share = span === 0 ? 1 : daysBetween(previous.date, date) / span;
      return previous.value + BigInt(Math.round(Number(value - previous.value) * share));
    }
    previous = { date: day, value };
  }
  return value;
}

/** Cash moving in or out of an investment account, as its provider reports it. */
export type InvestmentCashMove = { readonly type: string; readonly date: IsoDate; readonly amountMinor: bigint };

const abs = (value: bigint) => (value < 0n ? -value : value);

/**
 * An investment account's value on a past day: today's value less the money that came in after
 * that day (deposits, transfers, dividends) and plus what went out (withdrawals, fees). Growth is
 * not known day by day and stays with today. Before the first movement the account held nothing,
 * so a connection made today still has the history of the money paid in since it opened.
 */
export function investmentValueOn(currentMinor: bigint, moves: readonly InvestmentCashMove[], date: IsoDate): bigint {
  let later = 0n;
  for (const m of moves) {
    if (m.date <= date) continue;
    if (m.type === 'DEPOSIT' || m.type === 'DIVIDEND' || m.type === 'INTEREST') later += abs(m.amountMinor);
    else if (m.type === 'WITHDRAWAL' || m.type === 'FEE' || m.type === 'TAX') later -= abs(m.amountMinor);
    else if (m.type === 'TRANSFER') later += m.amountMinor;
  }
  const value = currentMinor - later;
  return value > 0n ? value : 0n;
}
